/**
 * Smoke, as numbers.
 *
 * A plume here is not a particle system: there is no emitter, no pool, and
 * nothing that accumulates. `count` puffs share one climb, spaced round it in
 * age, and each is reborn at the mouth of the pipe the moment it is spent. Ask
 * where puff `i` is at render second `t` and you get the same answer every
 * time, on every machine, however many frames it took to get there.
 *
 * That matters more here than realism does. A plume that is a pure function of
 * time cannot drift out of step while the tab is in the background, cannot
 * leak, cannot slow the game down the longer it is left running - and at
 * thirty metres, going past at 30 km/h, it is not something you could tell
 * from a plume that was simulated properly.
 *
 * No three.js in here: `World` owns the sprites, this owns where they go. Same
 * split as BeerSwing and the bottle, and for the same reason - it can be
 * stepped ten thousand times in a test with no canvas anywhere near it.
 */

import { SMOKE } from '../config/gameplay';
import { lerp } from '../utils/math';
import type { SmokeSpec } from '../world/Level';

/** One puff's fixed place in its plume. Authored once, at load, never stepped. */
export interface PuffSeed {
  /** Where it sits in the queue, 0..1. */
  phase: number;
  /** Which way it wanders off the line, radians. */
  wander: number;
  /** How fast it turns as it goes up. Signed: half of them go the other way. */
  spin: number;
}

/** Where a puff is this frame, and how much of it there is. */
export interface PuffPose {
  x: number;
  y: number;
  z: number;
  /** Metres. Doubled, this is the sprite's world size. */
  radius: number;
  /** 0..1, and 0 at both ends of the climb - which is what closes the loop. */
  opacity: number;
  /** Radians. */
  rotation: number;
}

export function puffPose(): PuffPose {
  return { x: 0, y: 0, z: 0, radius: 0, opacity: 0, rotation: 0 };
}

/**
 * The puffs of one plume: evenly spaced round the loop, then nudged.
 *
 * `rng` is expected to be a seeded generator called at load time and never
 * again - the same allowance src/utils/prng.ts is written for. Nothing about a
 * plume is random once the level is built.
 */
export function plumeSeeds(count: number, rng: () => number): PuffSeed[] {
  const seeds: PuffSeed[] = [];
  for (let i = 0; i < count; i++) {
    seeds.push({
      phase: (i + rng() * SMOKE.jitter) / count,
      wander: rng() * Math.PI * 2,
      spin: (rng() - 0.5) * SMOKE.spin,
    });
  }
  return seeds;
}

/**
 * Puff `seed` of plume `spec`, at render second `renderTime`, into `out`.
 *
 * Written into a caller-owned pose rather than returned, because this runs
 * once per puff per frame and a plume that allocates twenty objects a frame is
 * a plume that shows up in a profile for no reason at all.
 */
export function samplePuff(
  spec: SmokeSpec,
  seed: PuffSeed,
  renderTime: number,
  out: PuffPose,
): PuffPose {
  // Age in the loop: 0 at the mouth, 1 when spent. Every puff walks the same
  // climb a fixed distance behind the one in front of it.
  const cycle = renderTime / spec.life + seed.phase;
  const age = cycle - Math.floor(cycle);

  const downwind = Math.pow(age, SMOKE.lean);
  const sideways = Math.sin(seed.wander + age * SMOKE.wanderRate) * SMOKE.wander * age;

  out.x = spec.origin.x + spec.drift[0] * downwind + Math.cos(seed.wander) * sideways;
  out.y = spec.origin.y + spec.rise * Math.pow(age, SMOKE.climb);
  out.z = spec.origin.z + spec.drift[1] * downwind + Math.sin(seed.wander) * sideways;

  out.radius = lerp(spec.startRadius, spec.endRadius, Math.pow(age, SMOKE.spread));
  // Up to full almost at once, then thinning for the whole of the rest of the
  // climb: smoke does not fade evenly, it disappears at the top.
  out.opacity =
    spec.density * Math.min(1, age / SMOKE.fadeIn) * Math.pow(1 - age, SMOKE.fadeOut);
  out.rotation = seed.wander + seed.spin * age;

  return out;
}
